import React, { PureComponent, ReactElement } from "react";
import styled from "styled-components/macro";
import { Portal } from "react-portal";
import { connect } from "react-redux";
import { EDITBOX_BORDER, SELECT_GREEN } from "src/gantt/helpers/Const";
import { IconSettingsButton22x22 } from "../common/Buttons";

import removeTaskIcon from "../../../icons/IconTooltipDelete.png";
import removeTaskIconHover from "../../../icons/IconTooltipDeleteHover.png";
import removeTaskIconPressed from "../../../icons/IconTooltipDeletePressed.png";
import { Dispatch } from "src/rematch/store";
import { Action } from "@rematch/core";
import { IItem } from "src/backend/interfaces/IItem";

const imgs = [removeTaskIcon, removeTaskIconHover, removeTaskIconPressed];

const LINK_BUTTON_BG_SIZE = 200;

const SSHAPE_SIDE_WIDTH = 20;
const ARROW_LENGHT = 4;
const SSHAPE_REQUIRED_WIDTH = 30;

const LinkPath = styled.path`
  pointer-events: stroke;
  cursor: pointer;
`;

interface IDeleteLinkBaseProps {
  left: number;
  top: number;
}

const DeleteLinkBase = styled.div`
  left: ${(props: IDeleteLinkBaseProps) => (props.left - LINK_BUTTON_BG_SIZE / 2)}px;
  top: ${(props: IDeleteLinkBaseProps) => (props.top - LINK_BUTTON_BG_SIZE / 2 - 30)}px;
  width: ${LINK_BUTTON_BG_SIZE}px;
  height: ${LINK_BUTTON_BG_SIZE}px;
  position: absolute;
  z-index: 100;

  display: flex;
  align-items: center;
  justify-content: center;

  pointer-events: none;
`;

// For whatever reason, having the outermost element catch pointer elements disables scrolling events from going through to the viewport.
// Having this intermediate element seems to solve that issue. CSS mysteries.
const DeleteLinkHoverWatcher = styled.div`
  height: 100%;
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;
`;

const DeleteLinkTooltip = styled.div`
  border: 1px solid ${EDITBOX_BORDER};
  background: white;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);
  padding: 5px;

  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;
`;

export interface IPos {
  x: number;
  y: number;
}

interface ILinkProps {
  startTask: IItem;
  endTask?: IItem;
  startPos: IPos;
  endPos: IPos;
  drawStraight?: boolean;
  isSelected?: boolean;

  selectLink?: (startTaskId: number, endTaskId: number) => void;
  toggleLink: (linkFromId: number, linkToId: number) => Action<{ linkFromId: number, linkToId: number }, void>;
}

class Link extends PureComponent<ILinkProps> {
  clickCoords: IPos = { x: 0, y: 0 };

  componentDidMount = () => {
    for (const imgURL of imgs) {
      const img = new Image();
      img.src = imgURL;
    }
  };

  calcNormCoordinates = (): { cpt1: IPos, cpt2: IPos } => {
    const middle = this.props.startPos.x + (this.props.endPos.x - this.props.startPos.x) / 2;
    const cpt1 = { x: middle, y: this.props.startPos.y };
    const cpt2 = { x: middle, y: this.props.endPos.y };
    return { cpt1: cpt1, cpt2: cpt2 };
  };

  calcSCoordinates = (): { cpt1: IPos, cpt2: IPos, cpt3: IPos, cpt4: IPos } => {
    const cpt1 = {
      x: this.props.startPos.x + SSHAPE_SIDE_WIDTH,
      y: this.props.startPos.y,
    };
    const halfY = (this.props.endPos.y - this.props.startPos.y) / 2;
    const cpt2 = { x: cpt1.x, y: cpt1.y + halfY };
    const cpt3 = { x: this.props.endPos.x - SSHAPE_SIDE_WIDTH, y: cpt2.y };
    const cpt4 = { x: cpt3.x, y: cpt3.y + halfY };
    return { cpt1: cpt1, cpt2: cpt2, cpt3: cpt3, cpt4: cpt4 };
  };

  getPath = (): string => {
    if (this.props.drawStraight) {
      return `M${this.props.startPos.x} ${this.props.startPos.y}  ${this.props.endPos.x} ${this.props.endPos.y}`;
    } else if (this.props.startPos.x + SSHAPE_REQUIRED_WIDTH >= this.props.endPos.x) {
      const coordinates = this.calcSCoordinates();
      return `M${this.props.startPos.x} ${this.props.startPos.y}  ${coordinates.cpt1.x} ${coordinates.cpt1.y} ${coordinates.cpt2.x} ${
        coordinates.cpt2.y
      } ${coordinates.cpt3.x} ${coordinates.cpt3.y} ${coordinates.cpt4.x} ${coordinates.cpt4.y} ${this.props.endPos.x - ARROW_LENGHT} ${
        this.props.endPos.y
      }`;
    } else {
      const coordinates = this.calcNormCoordinates();
      return `M${this.props.startPos.x} ${this.props.startPos.y}  ${coordinates.cpt1.x} ${coordinates.cpt1.y} ${coordinates.cpt2.x} ${
        coordinates.cpt2.y
      } ${this.props.endPos.x - ARROW_LENGHT} ${this.props.endPos.y}`;
    }
  };

  clickLink = (e) => {
    if (this.props.endTask) {
      this.props.selectLink(this.props.startTask.id, this.props.endTask.id);
      this.clickCoords = { x: e.pageX, y: e.pageY };
    }
  }

  render(): ReactElement {
    const path = this.getPath();
    return (
      <g>
        <LinkPath
          stroke={this.props.isSelected ? SELECT_GREEN : "white"}
          d={path}
          strokeLinejoin="round"
          fill="transparent"
          strokeWidth="4"
          onClick={(e) => this.clickLink(e)}
        />

        <LinkPath
          style={{ userSelect: "none" }}
          stroke="black"
          d={path}
          strokeLinejoin="round"
          fill="transparent"
          strokeWidth="1"
          markerEnd={"url(#arrow)"}
          onClick={(e) => this.clickLink(e)}
        />
        {this.props.isSelected ? (
          <Portal>
            <DeleteLinkBase left={this.clickCoords.x} top={this.clickCoords.y}>
              <DeleteLinkHoverWatcher
                onMouseLeave={(_e) => {
                  this.props.selectLink(null, null);
                }}
              >
                <DeleteLinkTooltip>
                  <IconSettingsButton22x22
                    onClick={(_e) => {
                      this.props.selectLink(null, null);
                      this.props.toggleLink(this.props.startTask.id, this.props.endTask.id);
                    }}
                    image={removeTaskIcon}
                    imageHover={removeTaskIconHover}
                    imagePressed={removeTaskIconPressed}
                  />
                </DeleteLinkTooltip>
              </DeleteLinkHoverWatcher>
            </DeleteLinkBase>
          </Portal>
        ) : null}
      </g>
    );
  }
}

const mapDispatch = (dispatch: Dispatch) => ({
  toggleLink: (linkFromId: number, linkToId: number) => dispatch.items.toggleLink({ linkFromId, linkToId }),
});

export default connect(null, mapDispatch)(Link);
